// The flexitime confirmation email link (/bookings/<id>/<verb>): which state
// the link puts the booking in. Ported unchanged from v1's
// app/(school)/(chrome)/bookings/[id]/[hash]/deriveFlexitimeConfirmationState.ts,
// itself the state logic of the legacy class page's componentDidMount. A
// copy, not an import, on purpose: this package can't import the widget
// app (apps/widget), so keep the two in step by hand.

export type FlexitimeConfirmationState =
  | "no-changes"
  | "flexitime-about-to-confirm"
  | "flexitime-confirm"
  | "flexitime-about-to-reschedule"
  | "flexitime-about-to-reject"
  | "confirmed"
  | "about-to-reject";

/**
 * An already-confirmed booking: nothing changes. An anytime ("flexitime")
 * booking: confirm (online, the host still has to agree a place: "about to
 * confirm"; at a venue: confirmed as is), reschedule, or anything else is a
 * rejection. A regular booking: confirm, or reject. The non-anytime branch
 * of the original also called a `rejectBooking()` that could never run (it
 * sat behind `anytimeClass` inside the not-anytime branch); only the
 * reachable "about-to-reject" is kept.
 */
export function deriveFlexitimeConfirmationState({
  isAlreadyConfirmed,
  isAnytimeClass,
  isPhysicalLocation,
  confirmationAction,
}: {
  isAlreadyConfirmed: boolean;
  isAnytimeClass: boolean;
  isPhysicalLocation: boolean;
  confirmationAction: string | undefined;
}): FlexitimeConfirmationState {
  if (isAlreadyConfirmed) return "no-changes";

  if (isAnytimeClass) {
    if (confirmationAction === "confirm") {
      return isPhysicalLocation ? "flexitime-confirm" : "flexitime-about-to-confirm";
    }
    if (confirmationAction === "reschedule") return "flexitime-about-to-reschedule";
    return "flexitime-about-to-reject";
  }

  return confirmationAction === "confirm" ? "confirmed" : "about-to-reject";
}
